import * as _ from 'underscore';

import { SignInUrlBuilder } from '@tableau/shared-urls';

import { LoginUrlsWrapper } from '../../app/spaces/Login/LoginUrls';
import { Messages } from '../../codegen/Localize';
import { UserAgentSupport } from '../../common/utilities/UserAgentSupport';
import { FeatureFlags } from '../FeatureFlags';
import { ILocLinkifierModel, LocLinkify } from '../filters/LocLinkifierFilter';
import { CommonErrorCode, LoginErrorCodes, UserErrorCode } from '../helpers/ErrorCodes';
import { Keys } from '../helpers/Keys';
import { L10n } from '../L10n/L10n';
import { Module } from '../Module';
import { IError } from '../ServerApi/types/IError';
import { IGetSiteSamlLoginInfoResult } from '../ServerApi/types/IGetSiteSamlLoginInfoResult';
import { AuthenticationType, IPreloginCustomization } from '../ServerApi/types/IServerSettingsUnauthenticated';
import { ErrorMessageHandler } from '../services/ErrorMessageHandler';
import { ExternalAuthService, IExternalLoginEntryPoint } from '../services/ExternalAuthService';
import { ServerService } from '../services/ServerService';
import { SiteSwitchService } from '../services/SiteSwitchService';
import { StartPageService } from '../services/StartPageService';
import { UserLockoutMessageService } from '../services/UserLockoutMessageService';
import { WindowLocationService } from '../services/WindowLocationService';
import { WindowPopupService } from '../services/WindowPopupService';

export interface ILoginScope extends ng.IScope {
  loginForm: ng.IFormController;
  signinQueryParams: SignInUrlBuilder.QueryParams;
  maybeLogin: () => void;
  // Nested to avoid the dot problem
  credentials: {
    username: string;
    password: string;
  };
  errorMessages: ILocLinkifierModel[];
  busy: boolean;
  autoLogin: () => ng.IPromise<void>;
  isUsernameAndPasswordEnabled: () => boolean;
  shouldDisplayWindowsCredentialsLink: () => boolean;
  windowsCredentialsDisplayName: () => string;
  autoLoginDidFail: boolean;
  initialized: boolean;
  preloginCustomization: IPreloginCustomization;
  isSiteSamlEnabled: () => boolean;
  shouldBypassSiteSaml: boolean;
  requestPasswordReset: () => string;
  passwordResetEnabled: () => boolean;
  passwordResetSuccess: boolean;
  isTouch: () => boolean;
  signInNoteEnabled: boolean;
  getSignInNoteText: () => string;
  getSignInNoteUrl: () => string;
  handleUsernameKeyEvent: (event: JQueryEventObject) => void;
  handlePasswordKeyEvent: (event: JQueryEventObject) => void;
}

export class LoginCtrl {

  public static $inject = [
    '$scope',
    'ServerService',
    '$location',
    '$cookies',
    'ExternalAuthService',
    'StartPageService',
    'WindowLocationService',
    'WindowPopupService',
    'ErrorMessageHandler',
    'UserLockoutMessageService',
  ];

  constructor(
    $scope: ILoginScope,
    server: ServerService,
    $location: ng.ILocationService,
    $cookies: ng.cookies.ICookiesService,
    externalAuthService: ExternalAuthService,
    startPageService: StartPageService,
    windowLocationService: WindowLocationService,
    windowPopupService: WindowPopupService,
    errorMessageHandler: ErrorMessageHandler,
    userLockoutMessageService: UserLockoutMessageService,
  ) {
    // comes from the SignInProvider - angular
    const signinQueryParams = $scope.signinQueryParams;

    // Now that we instantiate this ctrl/pug from an AngularDirective
    // it seems the angular form / custom directives (tbSignInLogo)
    // don't play nice.  They attach to the child scope instead of the scope of the controller.
    function loginForm(): ng.IFormController {
      return _.isUndefined($scope.loginForm) ? ($scope as any).$$childHead.loginForm
        : $scope.loginForm;
    }

    $scope.passwordResetSuccess = signinQueryParams.passwordResetSuccess;
    $scope.$watch('credentials.username', () => {
      $scope.shouldBypassSiteSaml = false;
    });

    $scope.signInNoteEnabled = server.isSignInNoteEnabled();

    $scope.getSignInNoteText = () => {
      return server.getSignInNoteText();
    };

    $scope.getSignInNoteUrl = () => {
      return server.getSignInNoteUrl();
    };

    $scope.requestPasswordReset = () => {
      if ($scope.preloginCustomization && $scope.preloginCustomization.resetPasswordUrl) {
        return $scope.preloginCustomization.resetPasswordUrl;
      } else {
        return LoginUrlsWrapper.getInstance().requestPasswordResetUrl();
      }
    };

    $scope.passwordResetEnabled = () => {
      return (!!$scope.preloginCustomization && !!$scope.preloginCustomization.resetPasswordUrl)
          || (server && server.isPasswordResetEnabled());
    };

    $scope.isTouch = () => {
      return UserAgentSupport.isTouch();
    };

    function authenticatedRedirect() {
      // if we are a popup, window simply gets closed, no need to redirect
      if (windowPopupService.maybeNotifyAndClose()) {
        return;
      }

      // if we're not a popup, redirect user to the appropriate place
      const redirect = signinQueryParams.redirect;
      const externalRedirect = signinQueryParams.externalRedirect;
      const isSigninToSpecificSite = _.isString(signinQueryParams.site);

      // If this is a multisite session and the user did not specify a url,
      // let the user choose which site to enter
      if (externalRedirect) {
        // External: does not require hash prefix
        windowLocationService.setPathQueryFragment(externalRedirect);
      } else if (redirect) {
        // In-app: uses hash prefix
        $location.url(redirect);
      } else if (server.isSessionMultiSite() && !isSigninToSpecificSite) {
        LoginUrlsWrapper.navigateToLoginSitePicker();
      } else {
        $location.url(startPageService.getCurrentUserStartPageForCurrentSite());
      }
    }

    function handleLoginErrorCode(errorCode: number) {
      $scope.passwordResetSuccess = false;
      $scope.errorMessages = errorMessageHandler.getSignInErrorMessages(errorCode);
      if (errorCode === LoginErrorCodes.KerberosFailed || errorCode === LoginErrorCodes.SspiFailed) {
        $scope.autoLoginDidFail = true;
      }
      if (FeatureFlags.localAuthPasswordExpirationEnabled && errorCode === UserErrorCode.UserMustResetPassword) {
        LoginUrlsWrapper.navigateToPasswordResetWithUsername($scope.credentials.username);
      }
      angular.element('input[name="username"]').focus();
    }

    function buildExternalLoginEntryPoint(getSiteSamlLoginInfoResult?: IGetSiteSamlLoginInfoResult): IExternalLoginEntryPoint {
      return {
        path: null,
        queryParams: {
          authSetting: getSiteSamlLoginInfoResult ? getSiteSamlLoginInfoResult.authSettings : 'SAML',
          embedded: getSiteSamlLoginInfoResult ? getSiteSamlLoginInfoResult.embeddedAuth : false,
          idp: server.getSamlServiceEntityId(),
          sendPodInfo: false,
          siteLuid: getSiteSamlLoginInfoResult ? getSiteSamlLoginInfoResult.siteLuid : '',
          target_site: getSiteSamlLoginInfoResult ? getSiteSamlLoginInfoResult.urlNamespace : ''
        }
      };
    }

    $scope.maybeLogin = () => {
      $scope.errorMessages = [];
      if (server.authenticationType() === AuthenticationType.ClientCertificate) {
        // Username and password is not valid for client certificate without fallback, so attempt to autoLogin
        $scope.autoLogin();
      } else if ($scope.isSiteSamlEnabled()) {
        $scope.busy = true;
        server.siteSamlLogin($scope.credentials.username)
            .then(
            (getSiteSamlLoginInfoResult: IGetSiteSamlLoginInfoResult) => {
              const externalLoginEntryPoint = buildExternalLoginEntryPoint(getSiteSamlLoginInfoResult);
              externalAuthService.login(signinQueryParams, externalLoginEntryPoint);
            },
            (error: IError) => {
              const errorCode = error && error.code || <any>error;
              if (errorCode === UserErrorCode.UserNotSupportedGuestOperation) {
                // do default Auth if UserNotSupported
                if (server.isAuthenticationOpenId() || isSamlAuthenticationAndDesktopSamlSupported()) {
                  // default Auth is SAML
                  externalAuthService.login(signinQueryParams);
                } else {
                  // default Auth is local
                  $scope.shouldBypassSiteSaml = true;
                }
              } else {
                handleLoginErrorCode(errorCode);
              }
              $scope.busy = false;
            });
      } else if (loginForm().$valid) {
        $scope.errorMessages = [];
        $scope.busy = true;
        server.login($scope.credentials.username, $scope.credentials.password, signinQueryParams.site)
            .then(
            authenticatedRedirect,
            (error: IError) => {
              if (error && error.code === CommonErrorCode.RedirectToOtherPod) {
                let redirectUrl = signinQueryParams.externalRedirect;
                if (!redirectUrl && _.isString(signinQueryParams.redirect)) {
                  redirectUrl = '/#' + signinQueryParams.redirect;
                }
                if (!redirectUrl && _.isString(signinQueryParams.site)) {
                  redirectUrl =  SiteSwitchService.getSiteUrlFragment(signinQueryParams.site);
                }
                if (!redirectUrl) {
                  redirectUrl = '/#' + $location.url();
                }
                server.redirectToOtherPod(error, redirectUrl);
                return;
              }
              // Clear the password if credentials failed
              $scope.credentials.password = '';

              // This is really not how error should be handled. However, somehow in the development of the project,
              // some promise are rejected using ServerAPI.IError, and other simply with the error code. For example,
              // 401 errors returns only the code. To handle those scenarios, the error is coerced to either
              // error.code or simply error. In the future, it would be important to have a standard on how promises
              // can be rejected. A suggested approach is that promises should always be rejected with an instance of Error.
              const errorCode = error && error.code || <any>error;
              handleLoginErrorCode(errorCode);

              if (FeatureFlags.localAuthLoginRateLimitingEnabled && server.isServerDomainFamilyLocal()) {
                waitOnLoginRateLimiting(errorCode);
              } else {
                $scope.busy = false;
              }
            });
      } else if (loginForm().$error.required) {
        $scope.passwordResetSuccess = false;
        if (!$scope.credentials.username) {
          $scope.errorMessages = [LocLinkify.linkifier(Messages.enterYourUsername())];
          angular.element('input[name="username"]').focus();
        } else if (!$scope.credentials.password) {
          $scope.errorMessages = [LocLinkify.linkifier(Messages.enterYourPassword())];
          angular.element('input[name="password"]').focus();
        }
      }
    };

    $scope.shouldDisplayWindowsCredentialsLink = () => {
      return server.isWindowsCredentialsAutoLogin() && !$scope.autoLoginDidFail;
    };

    $scope.isUsernameAndPasswordEnabled = () => {
      return (server.isSiteSamlEnabled() && server.isServerDomainFamilyLocal()) ||  isDesktopSamlDisabled() ||
        (!server.isAuthenticationExternal() && server.authenticationType() !== AuthenticationType.ClientCertificate);
    };

    $scope.isSiteSamlEnabled = () => {
      return server.isSiteSamlEnabled() && server.isServerDomainFamilyLocal() && !$scope.shouldBypassSiteSaml;
    };

    $scope.autoLogin = () => {
      $scope.busy = true;
      return server.autoLogin(signinQueryParams.site)
        .then(
          authenticatedRedirect,
          (errorCode: number) => {
            handleLoginErrorCode(errorCode);
            $scope.busy = false;
            $scope.initialized = true;
          }
        );
    };

    $scope.windowsCredentialsDisplayName = () => server.sessionUserDisplayName();

    const licenseExpirationDateIfWithinWarningPeriod = server.licenseExpirationDateIfWithinWarningPeriod();
    if (signinQueryParams.error) {
      // coerce to number
      const errorCode = signinQueryParams.error;
      handleLoginErrorCode(errorCode);
    } else if (licenseExpirationDateIfWithinWarningPeriod) {
      $scope.errorMessages = [
        LocLinkify.linkifier(Messages.licenseExpirationDateWarningMessage({
          LICENSE_EXPIRATION_DATE: L10n.localizedDateTime(licenseExpirationDateIfWithinWarningPeriod)
        }))
      ];
    }

    $scope.preloginCustomization = server.preloginCustomization();

    const allowAutoSignin = !signinQueryParams.disableAutoSignin;
    if (!server.inSession() && server.isAuthenticationAutoLogin() && allowAutoSignin) {
      // Attempt auto login unless there is a query parameter saying not to
      $scope.autoLogin();
    } else if (server.isSiteSamlEnabled() && server.isServerDomainFamilyLocal()) {
      $scope.initialized = true;
    } else if (server.isAuthenticationOpenId() || isSamlAuthenticationAndDesktopSamlSupported()) {
      externalAuthService.login(signinQueryParams);
    } else {
      $scope.initialized = true;
    }

    $scope.credentials = {
      username: '',
      password: ''
    };

    $scope.handleUsernameKeyEvent = ($event: JQueryEventObject) => {
      if($event.which === Keys.Escape) {
        $scope.credentials.username = '';
      }
    };

    $scope.handlePasswordKeyEvent = ($event: JQueryEventObject) => {
      if($event.which === Keys.Escape) {
        $scope.credentials.password = '';
      }
    };

    function isSamlAuthenticationAndDesktopSamlSupported(): boolean {
      if (server.isAuthenticationSaml()) {
        if (signinQueryParams.client_type === 'desktop') {
          return !server.isDesktopSamlDisabled();
        } else {
          return true;
        }
      }
      return false;
    }

    function isDesktopSamlDisabled() : boolean {
      return server.isAuthenticationSaml() &&
        (signinQueryParams.client_type === 'desktop') && server.isDesktopSamlDisabled();
    }

    function waitOnLoginRateLimiting(errorCode: number): void {
      if (loginForm().waitingOnRateLimiting) {
        return;
      }
      if (errorCode === UserErrorCode.LoginAttemptRejected || errorCode === LoginErrorCodes.BadCredentials) {
        loginForm().waitingOnRateLimiting = true;
        const originalMessages = $scope.errorMessages;

        userLockoutMessageService.showMessageForUser($scope.credentials.username, (message: string) => {
          // New message handler.
          const newMessages: ILocLinkifierModel[] = [...originalMessages];
          newMessages.push(LocLinkify.linkifier(message));
          $scope.errorMessages = newMessages;
        }, () => {
          // On complete.
          $scope.busy = false;
          loginForm().waitingOnRateLimiting = false;
          $scope.errorMessages = originalMessages;
        });
      } else {
        $scope.busy = false;
      }
    }
  }
}

Module.instance().controller('LoginCtrl', LoginCtrl);
